import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { pnrService } from '../services/pnrService';
import SweetAlert from "react-bootstrap-sweetalert";
import Select from 'react-select';
import './LockPNR.css';

const LockPNR = () => {
  const [formData, setFormData] = useState({
    ma_dat_cho: '',
    hang_bay: 'VN'
  });
  const [airlines, setAirlines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAirlines, setLoadingAirlines] = useState(true);
  const [errors, setErrors] = useState({
    hang_bay: '',
    ma_dat_cho: ''
  });
  const [sweetAlert, setSweetAlert] = useState({ isShow: false, baseProps: {} });
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Load danh sách hãng bay khi component mount
  useEffect(() => {
    const loadAirlines = async () => {
      try {
        setLoadingAirlines(true);
        const response = await pnrService.getAirlineList({ trang: 1, so_luong: 1000 });
        
        if (response.code === 200) {
          setAirlines(response?.data?.content || []);
        }
      } catch (error) {
        console.error('Error loading airlines:', error);
      } finally {
        setLoadingAirlines(false);
      }
    };

    loadAirlines();
  }, []);

  const onCancel = () => {
    setSweetAlert({ isShow: false, baseProps: {} });
  };

  const showSweetAlert = (params) => {
    setSweetAlert((preState) => ({
      ...preState, isShow: true,
      baseProps: {
        title: "Thông báo",
        show: true,
        showCloseButton: true,
        showCancel: false,
        showConfirm: false,
        type: params.code === 200 ? 'success' : 'error',
        onConfirm: onCancel,
        onCancel: onCancel,
        children: <p>{params.message}</p>,
      }   
    }));
  };

  const showConfirmAlert = (message, onConfirm, type = 'warning') => {
    setSweetAlert({
      isShow: true,
      baseProps: {
        title: "Xác nhận",
        show: true,
        type: type,
        showCloseButton: true,
        showCancel: true,
        confirmBtnText: "Xác nhận",
        cancelBtnText: "Hủy",
        confirmBtnBsStyle: "success",
        cancelBtnBsStyle: "danger",
        onConfirm: onConfirm,
        onCancel: onCancel,
        children: <p>{message}</p>,
      }
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      navigate('/login');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error của field này
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleAirlineChange = (selectedOption) => {
    setFormData({
      ...formData,
      hang_bay: selectedOption ? selectedOption.value : ''
    });
    // Clear error của hãng bay
    if (errors.hang_bay) {
      setErrors({
        ...errors,
        hang_bay: ''
      });
    }
  };

  const handleSearch = async () => {
    const newErrors = {};
    
    if (!formData.ma_dat_cho.trim()) {
      newErrors.ma_dat_cho = 'Vui lòng nhập mã đặt chỗ';
    }
    if (!formData.hang_bay) {
      newErrors.hang_bay = 'Vui lòng chọn hãng bay';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors({ ...errors, ...newErrors });
      return;
    }

    setLoading(true);
    setErrors({ hang_bay: '', ma_dat_cho: '' });

    try {
      const response = await pnrService.searchPNR(formData);
      
      if (response.code === 200) {
        const message = response.message;
        showConfirmAlert(message, () => {
          // Kiểm tra nếu message chứa text "chưa khoá"
          const shouldLock = message.includes("chưa khoá") ? 1 : 0;
          updateTicketStatus(shouldLock === 1);
        });
      } else {
        showSweetAlert({
          code: response.code,
          message: response.message,
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleLock = async () => {
    const newErrors = {};
    
    if (!formData.ma_dat_cho.trim()) {
      newErrors.ma_dat_cho = 'Vui lòng nhập mã đặt chỗ';
    }
    if (!formData.hang_bay) {
      newErrors.hang_bay = 'Vui lòng chọn hãng bay';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors({ ...errors, ...newErrors });
      return;
    }

    const message = `Bạn có muốn khoá PNR ${formData.ma_dat_cho} không?`;
    showConfirmAlert(message, () => updateTicketStatus(true));
  };

  const updateTicketStatus = async (shouldLock) => {
    try {
      const updateData = {
        ma_dat_cho: formData.ma_dat_cho,
        hang_bay: formData.hang_bay,
        is_lock: shouldLock ? 1 : 0
      };

      const response = await pnrService.updateTicketStatus(updateData);
      
      if(response?.code === 200) 
      {
        showSweetAlert({
          code: response.code,
          message: response.message,
        });
      }
      else
      {
        if(response?.data === null)
        {
          showSweetAlert({
            code: response.code,
            message: response.message,
          });
        }
        else
        {
          const message = response.message;
          showConfirmAlert(message, () => {
            updateTicketStatus(0);
          });
       }
      }
    } catch (error) {
    }
  };

  // Tạo options cho react-select
  const airlineOptions = airlines.map(airline => ({
    value: airline.ten_tat,
    label: `${airline.ten} (${airline.ten_tat})`
  }));

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      padding: '4px 8px',
      border: '1.5px solid #e2e8f0',
      borderRadius: '8px',
      backgroundColor: '#f7fafc',
      fontSize: '16px',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(74, 144, 164, 0.1)' : 'none',
      borderColor: state.isFocused ? '#4a90a4' : '#e2e8f0',
      '&:hover': {
        borderColor: '#4a90a4'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#4a90a4' : state.isFocused ? '#f0f4f8' : 'white',
      color: state.isSelected ? 'white' : '#2d3748'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#a0aec0'
    })
  };

  return (
    <div className="lock-pnr-container">
      <header className="lock-pnr-header">
        <div className="header-content">
          <div className="header-left">
            {/* <button onClick={() => navigate('/')} className="back-button">
              ← Quay lại
            </button> */}
            <h1>AGS99 PNR Management</h1>
          </div>
          <div className="user-info">
            <span>{user?.tai_khoan?.ho_ten}</span>
            <button onClick={handleLogout} className="logout-button">
              Đăng xuất
            </button>
          </div>
        </div>
      </header>

      <main className="lock-pnr-content">
        <div className="pnr-card">
          <div className="form-group">
            <label htmlFor="hang_bay">Hãng bay <span className="required">*</span></label>
            <Select
              options={airlineOptions}
              value={airlineOptions.find(option => option.value === formData.hang_bay) || null}
              onChange={handleAirlineChange}
              placeholder="Chọn hãng bay"
              isLoading={loadingAirlines}
              isDisabled={loading || loadingAirlines}
              styles={customStyles}
              isClearable
              isSearchable
            />
            {errors.hang_bay && (
              <div className="field-error">{errors.hang_bay}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="ma_dat_cho">Mã đặt chỗ <span className="required">*</span></label>
            <input
              type="text"
              id="ma_dat_cho"
              name="ma_dat_cho"
              value={formData.ma_dat_cho}
              onChange={handleChange}
              placeholder="Nhập mã đặt chỗ"
              disabled={loading}
              className="form-input"
            />
            {errors.ma_dat_cho && (
              <div className="field-error">{errors.ma_dat_cho}</div>
            )}
          </div>

          <div className="button-group">
            <button 
              type="button" 
              onClick={handleLock}
              className="btn btn-lock"
              disabled={loading || loadingAirlines}
            >
              Khoá
            </button>
            <button 
              type="button" 
              onClick={handleSearch}
              className="btn btn-search"
              disabled={loading || loadingAirlines}
            >
              {loading ? 'Đang tìm kiếm...' : 'Tìm kiếm'}
            </button>
          </div>
        </div>
      </main>
      
      {sweetAlert.isShow && (
        <SweetAlert
          {...sweetAlert.baseProps}
        />
      )}
    </div>
  );
};

export default LockPNR;